export function fontSize() {
    let size;
    const winW = window.innerWidth;

    size = Math.round(winW / 19.2 * 0.75);
    if (winW >= 1920) {
      size = 75;
    }
    return size;
}

export function _debounce(fn, wait) {
    let timeout;
    const debounced = function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            fn.apply(this, args);
        }, wait);
    };
    return debounced;
}

// 模块级别的变量，存储当前会话的 user_id
// 刷新页面时模块重新加载，变量会重新初始化，从而生成新的ID
let currentUserId = null;

/**
 * 生成唯一的用户ID
 * 使用时间戳 + 随机数生成，确保标识符唯一
 * 在同一个页面会话期间（不刷新）返回相同的ID
 * 刷新页面或关闭重开浏览器时会生成新的ID
 * @returns {string} 唯一的用户ID
 */
export function generateUserId() {
    // 如果已有ID，直接返回（同一会话期间）
    if (currentUserId) {
        return currentUserId;
    }
    
    // 生成新的ID：使用时间戳 + 随机数
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000000);
    currentUserId = `user_${timestamp}_${random}`;
    
    return currentUserId;
}

/**
 * 跨标签页事件通信工具
 * 使用 localStorage + storage 事件实现跨标签页通信
 */
const CROSS_TAB_EVENT_PREFIX = 'cross_tab_event_';

/**
 * 跨标签页触发事件
 * @param {Vue} vm - Vue 实例
 * @param {string} eventName - 事件名称
 * @param {*} data - 事件数据（可选）
 */
export function emitCrossTabEvent(vm, eventName, data = null) {
    // 1. 先触发本地 Vue 事件（当前标签页）
    if (vm && vm.$root) {
        vm.$root.$emit(eventName, data);
    }
    
    // 2. 写入 localStorage，触发其他标签页的 storage 事件
    const storageKey = `${CROSS_TAB_EVENT_PREFIX}${eventName}`;
    const eventData = {
        eventName,
        data,
        timestamp: Date.now(),
        random: Math.random() // 确保每次写入都不同，触发 storage 事件
    };
    try {
        localStorage.setItem(storageKey, JSON.stringify(eventData));
        // 立即删除，避免 localStorage 堆积
        setTimeout(() => {
            localStorage.removeItem(storageKey);
        }, 100);
    } catch (e) {
        console.warn('跨标签页事件发送失败:', e);
    }
}

/**
 * 监听跨标签页事件
 * @param {Vue} vm - Vue 实例
 * @param {string} eventName - 事件名称
 * @param {Function} handler - 事件处理函数
 * @returns {Function} 取消监听的函数
 */
export function onCrossTabEvent(vm, eventName, handler) {
    if (!vm || !vm.$root) {
        console.warn('Vue 实例无效，无法监听跨标签页事件');
        return () => {};
    }
    
    // 1. 监听本地 Vue 事件（当前标签页）
    vm.$root.$on(eventName, handler);
    
    // 2. 监听 storage 事件（来自其他标签页）
    const storageKey = `${CROSS_TAB_EVENT_PREFIX}${eventName}`;
    const storageHandler = (e) => {
        // 只处理相关的事件
        if (e.key !== storageKey || !e.newValue) {
            return;
        }
        
        try {
            const eventData = JSON.parse(e.newValue);
            // 触发本地的 Vue 事件
            vm.$root.$emit(eventName, eventData.data);
        } catch (e) {
            console.warn('解析跨标签页事件数据失败:', e);
        }
    };
    
    window.addEventListener('storage', storageHandler);
    
    // 返回取消监听的函数
    return () => {
        vm.$root.$off(eventName, handler);
        window.removeEventListener('storage', storageHandler);
    };
}

/**
 * 取消监听跨标签页事件
 * @param {Vue} vm - Vue 实例
 * @param {string} eventName - 事件名称
 * @param {Function} handler - 事件处理函数
 * @param {Function} offStorageHandler - storage 事件取消函数（由 onCrossTabEvent 返回）
 */
export function offCrossTabEvent(vm, eventName, handler, offStorageHandler) {
    if (vm && vm.$root) {
        vm.$root.$off(eventName, handler);
    }
    if (typeof offStorageHandler === 'function') {
        offStorageHandler();
    }
}